// SUB-OFFICE 7 — the petitioner. An articulated 10-box figure rig (head, torso,
// four arm/leg segments) shared with the ghost clerks via figure.ts — NOT a
// rounded capsule; the only "capsule" is the movement collider. v3-3 (§5.1
// Path A): the body is built brightened toward bone with a mild toy bulk on its
// two primary masses (head + torso) so the petitioner unmistakably pops from the
// ghost-grey crowd and is never mistaken for staff. One accent only: the carbon
// eye planes are the "visor", the red lanyard (grade >=2) the one job-color.
// Overhead designation + Standing sprite, 2.6 m/s shuffle with a 0.12s accel
// envelope, 4-degree lean, 1.5cm bob. Movement resolves against a capsule
// collider (RADIUS 0.35) that slides along walls; yellow-line / DO-NOT-STAND
// cells are counted silently — the Bureau files how you walk.

import {
  BoxGeometry, CanvasTexture, Color, Mesh, MeshBasicMaterial,
  MeshLambertMaterial, PlaneGeometry, Sprite, SpriteMaterial, Group, Vector3,
} from 'three';
import { PAL, mix, rgba, roomShadow } from '../palette';
import { SPAWN } from '../world/layout';
import { slideCapsule, zoneAt } from '../world/collision';
import { makeLabelTexture, mkCanvas } from '../render/textures';
import { patchMaterial } from '../render/scene';
import { FigureRig, attachFigureMeshes, type FigureScale } from './figure';
import type { Entity, EventBus } from './entities';

const MAX_SPEED = 2.6;
const HASTE_SPEED = 3.5; // AUTHORIZED HASTE (FORM 11) — toggle unlocked at grade 2
const ACCEL_T = 0.12;
const RADIUS = 0.35;

// v3-3 (§5.1 Path A, §7.6) — PLAYER-VS-CROWD READABILITY.
// The petitioner's body is brightened toward bone so it is unmistakable among
// the ghost-grey clerks (PAL.ghost). Set ~halfway ghost->bone: clearly lighter
// than the crowd, clearly dimmer than the bone walls, still a warm paper tone
// (not white). One tunable — the owner's diorama pass dials PLAYER_BRIGHTEN.
// (The spec's nominal "+0.05" toward bone lands within ~3 luma of the crowd —
// too subtle to satisfy the hard "never mistaken for staff" law — so it is
// dialed up to actually land; reduce here if the diorama wants it gentler.)
const PLAYER_BRIGHTEN = 0.5;
const PLAYER_BODY = mix(PAL.ghost, PAL.bone, PLAYER_BRIGHTEN);

// Mild toy bulk on the two PRIMARY masses only (head + torso), toward the chunky
// Among-Us read. A subtle proportion nudge, not a redesign; ghosts pass NO scale
// and stay staff-shaped. Torso height stays 1.0 so every rig joint and the
// front-mounted lanyard keep their exact positions; the widened head/torso
// depth is why the eye planes and lanyard are pushed a touch forward below.
const PLAYER_BULK: FigureScale = {
  torso: [1.16, 1.0, 1.16],
  head: [1.18, 1.06, 1.18],
};

// Warm interior shadow for the grounding discs (§5.2): the blob shadow is nudged
// off pure neutral carbon toward the building's warm carbon-brown room shadow so
// bodies feel planted in the room's temperature, not floating on dead grey.
const SHADOW_TINT = mix(PAL.carbon, roomShadow('hall'), 0.5);

export class Player implements Entity {
  readonly id = 'player';
  readonly group = new Group();
  readonly pos = new Vector3(SPAWN.x, 0, SPAWN.z);
  readonly vel = new Vector3();
  haste = false; // gameplay slice flips this after FORM 11

  private rig: FigureRig;
  private label: Sprite;
  private labelTex: CanvasTexture;
  private shadow: Mesh;
  private moveInput = { x: 0, z: 0 };
  private bobPhase = 0;
  private idleT = 0;
  private lastZone = 0;
  private yellowCrossings = 0;
  private noStandTime = 0;
  private facing = SPAWN.facing;
  private carryPaper: Mesh;
  private carryCanister: Mesh;
  private lanyard: Mesh; // grade >=2: a red staff strip at the chest
  private clipboard: Mesh; // grade >=3: a manila board in the left hand
  private carryKind: 'none' | 'paper' | 'canister' = 'none';
  private carryT = 0;
  private fp = false; // first person: no visible self-body except the shadow

  constructor(private bus: EventBus) {
    // the articulated petitioner: box rig, brightened toward bone + toy-bulked
    // (v3-3) so it pops from the ghost-grey crowd; walk cycle in figure.ts
    const mat = new MeshLambertMaterial({ color: new Color(PLAYER_BODY).convertSRGBToLinear(), flatShading: true });
    patchMaterial(mat);
    this.rig = new FigureRig();
    attachFigureMeshes(this.rig, mat, PLAYER_BULK);
    this.group.add(this.rig.root);

    // eyes: two carbon dots (the "visor") so the figure has a front. Pushed a
    // touch forward + wider to sit proud of the v3-3 fattened head face.
    const eyeMat = new MeshBasicMaterial({ color: new Color(PAL.carbon) });
    for (const dx of [-0.055, 0.055]) {
      const eye = new Mesh(new PlaneGeometry(0.04, 0.07), eyeMat);
      eye.position.set(dx, 0.02, -0.135);
      this.rig.nodes.head.add(eye);
    }

    // overhead label: a dash until the Bureau assigns a designation (M9)
    this.labelTex = makeLabelTexture('—', '');
    const lm = new SpriteMaterial({ map: this.labelTex, depthTest: false });
    this.label = new Sprite(lm);
    this.label.scale.set(1.7, 0.64, 1);
    this.label.position.y = 2.05;
    this.group.add(this.label);

    // blob shadow (no shadow maps exist on any tier); v3-3 (§5.2): warm-tinted
    // off pure neutral carbon so the petitioner is planted in the room's warmth
    const [c, ctx] = mkCanvas(64, 64);
    const g = ctx.createRadialGradient(32, 32, 4, 32, 32, 30);
    g.addColorStop(0, rgba(SHADOW_TINT, 0.42));
    g.addColorStop(1, rgba(SHADOW_TINT, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    const st = new CanvasTexture(c);
    this.shadow = new Mesh(
      new PlaneGeometry(1.1, 1.1),
      new MeshBasicMaterial({ map: st, transparent: true, depthWrite: false })
    );
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.position.y = 0.015;
    this.group.add(this.shadow);

    // the carried form: one unfinished sheet riding overhead with sine flutter
    const paperMat = new MeshBasicMaterial({
      color: new Color(PAL.paper), side: 2, // DoubleSide
    });
    this.carryPaper = new Mesh(new PlaneGeometry(0.42, 0.6), paperMat);
    this.carryPaper.position.y = 1.72;
    this.carryPaper.rotation.x = -Math.PI / 2.3;
    this.carryPaper.visible = false;
    this.group.add(this.carryPaper);

    // grade attachments: ride the figure nodes so FP-hide comes for free (the
    // rig is invisible in first person). Off until the Bureau reclassifies you.
    const lanyardMat = new MeshLambertMaterial({ color: new Color(PAL.stampRed).convertSRGBToLinear(), flatShading: true });
    patchMaterial(lanyardMat);
    this.lanyard = new Mesh(new BoxGeometry(0.03, 0.24, 0.02), lanyardMat);
    this.lanyard.position.set(0.05, 0.34, -0.135); // chest, proud of the v3-3 fattened torso face
    this.lanyard.visible = false;
    this.rig.nodes.torso.add(this.lanyard);

    const clipMat = new MeshLambertMaterial({ color: new Color(PAL.manila).convertSRGBToLinear(), flatShading: true });
    patchMaterial(clipMat);
    this.clipboard = new Mesh(new BoxGeometry(0.17, 0.23, 0.02), clipMat);
    this.clipboard.rotation.x = -0.4;
    this.clipboard.position.set(0.04, -0.32, 0.07); // the left hand grips it
    this.clipboard.visible = false;
    this.rig.nodes.armLL.add(this.clipboard);

    const canMat = new MeshBasicMaterial({ color: new Color(PAL.amber) });
    this.carryCanister = new Mesh(new PlaneGeometry(0.16, 0.5), canMat);
    this.carryCanister.position.y = 1.7;
    this.carryCanister.rotation.x = -Math.PI / 2.5;
    this.carryCanister.visible = false;
    this.group.add(this.carryCanister);

    this.group.position.copy(this.pos);
  }

  /** CARRY: one unfinished form (or rolled canister) rides visibly overhead. */
  setCarry(kind: 'none' | 'paper' | 'canister'): void {
    this.carryKind = kind;
    this.carryPaper.visible = kind === 'paper' && !this.fp;
    this.carryCanister.visible = kind === 'canister' && !this.fp;
  }

  get carrying(): 'none' | 'paper' | 'canister' {
    return this.carryKind;
  }

  /** Grade dress: the lanyard is issued at PROBATIONARY ASSET (tier 2), the
   *  clipboard at the tier above it. Called whenever the Bureau reclassifies. */
  setGradeTier(tier: number): void {
    this.lanyard.visible = tier >= 2;
    this.clipboard.visible = tier >= 3;
  }

  /** FP: the petitioner does not see themselves; the blob shadow remains. */
  setFirstPerson(on: boolean): void {
    this.fp = on;
    this.rig.root.visible = !on;
    this.label.visible = !on;
    this.setCarry(this.carryKind);
  }

  /** FP standing turns: the body silently adopts the look heading. */
  setFacing(theta: number): void {
    this.facing = theta;
  }

  /**
   * Teleport the capsule (zone transitions / spawn selection). Clears velocity
   * and stops any in-progress slide so the new zone's grid resolves cleanly.
   * facing is optional; the exterior spawns you facing the door.
   */
  setPosition(x: number, z: number, facing?: number): void {
    this.pos.set(x, 0, z);
    this.vel.set(0, 0, 0);
    this.moveInput.x = 0;
    this.moveInput.z = 0;
    this.lastZone = 0;
    this.noStandTime = 0;
    if (facing !== undefined) this.facing = facing;
    this.group.position.set(x, 0, z);
  }

  /** Last walked/looked heading (view toggles keep the gaze continuous). */
  get heading(): number {
    return this.facing;
  }

  /** Called by input each frame; unit-ish vector in world axes. */
  setMove(x: number, z: number): void {
    this.moveInput.x = x;
    this.moveInput.z = z;
  }

  setDesignation(designation: string, standing: string): void {
    this.labelTex.dispose();
    this.labelTex = makeLabelTexture(designation, standing);
    (this.label.material as SpriteMaterial).map = this.labelTex;
    (this.label.material as SpriteMaterial).needsUpdate = true;
  }

  update(dt: number): void {
    const max = this.haste ? HASTE_SPEED : MAX_SPEED;
    const mLen = Math.hypot(this.moveInput.x, this.moveInput.z);
    const tx = mLen > 0 ? (this.moveInput.x / Math.max(1, mLen)) * max : 0;
    const tz = mLen > 0 ? (this.moveInput.z / Math.max(1, mLen)) * max : 0;
    const k = Math.min(1, dt / ACCEL_T);
    this.vel.x += (tx - this.vel.x) * k;
    this.vel.z += (tz - this.vel.z) * k;
    if (Math.abs(this.vel.x) < 0.01) this.vel.x = 0;
    if (Math.abs(this.vel.z) < 0.01) this.vel.z = 0;

    const speed = Math.hypot(this.vel.x, this.vel.z);
    if (speed > 0.01) {
      const r = slideCapsule(this.pos.x, this.pos.z, this.vel.x * dt, this.vel.z * dt, RADIUS);
      this.pos.x = r.x;
      this.pos.z = r.z;
      if (r.hitX) this.vel.x = 0;
      if (r.hitZ) this.vel.z = 0;
      this.facing = Math.atan2(this.vel.x, this.vel.z);
    }

    // silent zone accounting — the Bureau is never wrong about where you stood
    const zone = zoneAt(this.pos.x, this.pos.z);
    if ((zone & 2) !== 0 && (this.lastZone & 2) === 0) {
      this.yellowCrossings++;
      this.bus.emit('telemetry', { kind: 'yellow', count: this.yellowCrossings });
      if (__DEV__) console.log('[telemetry] yellow-line crossings:', this.yellowCrossings);
    }
    if ((zone & 4) !== 0) {
      this.noStandTime += dt;
      if (this.noStandTime > 3 && this.noStandTime - dt <= 3) {
        this.bus.emit('telemetry', { kind: 'noStand', count: Math.round(this.noStandTime) });
      }
    } else {
      this.noStandTime = 0;
    }
    this.lastZone = zone;

    // body language: articulated stride, lean into heading, footstep cadence
    const moving = speed > 0.15;
    this.idleT += dt;
    if (moving) {
      this.bobPhase += dt * (speed / max) * 11;
      const prevSin = Math.sin(this.bobPhase - dt * 11);
      const curSin = Math.sin(this.bobPhase);
      if (prevSin <= 0 && curSin > 0) this.bus.emit('audio:step');
    }
    const lean = moving ? (4 * Math.PI) / 180 : 0;

    this.rig.root.rotation.y = this.facing;
    this.rig.root.rotation.x = lean * (speed / max);
    this.rig.pose({ phase: this.bobPhase, speed01: moving ? speed / max : 0, idleT: this.idleT });

    this.group.position.set(this.pos.x, 0, this.pos.z);
    // overhead paper/label lag: the sprite trails three frames of motion
    this.label.position.x = -this.vel.x * 0.05;
    this.label.position.z = -this.vel.z * 0.05;

    // carried paperwork lags the same three frames and flutters in sine
    if (this.carryPaper.visible || this.carryCanister.visible) {
      this.carryT += dt;
      const flutter = Math.sin(this.carryT * 5.2) * 0.07;
      const carried = this.carryPaper.visible ? this.carryPaper : this.carryCanister;
      carried.position.x = -this.vel.x * 0.055;
      carried.position.z = -this.vel.z * 0.055;
      carried.rotation.z = flutter;
      carried.position.y = (this.carryPaper.visible ? 1.72 : 1.7) + Math.sin(this.carryT * 2.1) * 0.02;
    }
  }
}

declare const __DEV__: boolean;
